// pipe (responsive ERP): the ONE place the ERP grids' shared style CONTRACT is declared (SSOT) —
// the responsive geometry, the selectors card mode is built from, and the "this cell holds a
// control" predicate the grid's width discipline and its copy gestures both key off.
//
// WHY A MODULE AND NOT A LITERAL IN EACH css.ts — the threshold below is DERIVED from the column
// floor, exactly like the reports param-grid's two-track breakpoint: a hardcoded number silently
// dead-wires the moment the other number changes. Both the styles (erp-screen.css.ts,
// erp-table.css.ts) and the component that stamps the column count (erp-table.tsx) read it from
// here, so there is never a second copy to drift.
//
// The same argument is why the SELECTORS live here as builders (see the card-mode contract at the
// bottom) rather than being spelled out inline in the stylesheet: card mode is pure CSS inside a
// `@container` block, which no DOM test environment evaluates — but a selector STRING can be run
// against a real DOM with `Element.matches`, which is what lets `erp-table.spec.tsx` pin "a colSpan
// panel is not a card line" and "the totals label is not a column value" for real.
//
// WHY VIEWPORT-INDEPENDENT (load-bearing) — a phone hitting app.pipe.monster gets the DESKTOP
// bundle: `apps/web/src/app.tsx` imports `@affine/core/desktop/router` unconditionally and the
// mobile edition carries no ERP routes, so `BUILD_CONFIG.isMobileEdition` and the `data-mobile`
// attribute are FALSE on an iPhone here. The responsive switch is therefore a CONTAINER query on
// the grid's own scroll region — which is also the more correct axis than the viewport, because
// the workbench splits into side-by-side panes (the same argument reports.css.ts makes for its
// param grid).

/**
 * The narrowest a single grid column may resolve to before its content is unreadable.
 *
 * The shared `Table` lays out `table-layout: fixed; width: 100%` with no per-column width, so N
 * columns divide the pane EQUALLY and the table can never exceed it — 12 report columns in a
 * 342px phone pane is ~28px each, i.e. nothing. Multiplying this floor by the live column count
 * gives the table a real `min-width`, which is what finally makes `gridWrapper`'s existing
 * `overflow: auto` scroll instead of squeezing.
 */
export const ERP_COL_MIN_WIDTH = 120;

/** The fewest columns that still read as a TABLE rather than as a list of values. */
export const ERP_TABLE_MIN_COLS = 4;

/**
 * The pane width at/above which an ERP grid renders as a TABLE. Below it every row renders as a
 * stacked label/value card. Derived — never tune this directly, tune the two constants above.
 *
 * This is the ERP surface's ONE responsive threshold; every ERP style and component reads it from
 * here. Two neighbours are deliberately NOT folded in, because a shared constant must be one
 * CONCEPT, not one number: `reports.css.ts`'s `PARAM_TWO_TRACK_MIN` is a grid-TRACK arithmetic
 * derived from that form's own track floor (a different measurement of a different box), and the
 * ~25 literal pixel breakpoints elsewhere in `core/src` are inherited upstream AFFiNE code on
 * surfaces this module does not own — retrofitting them is a separate sweep, not this fix.
 */
export const ERP_TABLE_MIN_WIDTH = ERP_COL_MIN_WIDTH * ERP_TABLE_MIN_COLS;

/** The query container every ERP grid's scroll region (`gridWrapper`) establishes. */
export const ERP_GRID_CONTAINER = 'pipe-erp-grid';

/**
 * The narrow (card-mode) container query. Range syntax, like the reports param grid's — container
 * queries and range syntax shipped together (Chrome 105 / Safari 16), so anything that can run a
 * container query can parse this.
 */
export const ERP_NARROW_CONTAINER = `${ERP_GRID_CONTAINER} (width < ${ERP_TABLE_MIN_WIDTH}px)`;

/**
 * The same threshold as a MEDIA query, for page chrome that cannot query itself (an element is
 * never its own container). Classic `max-width` syntax deliberately: range syntax in *media*
 * queries only reached Safari 16.4, later than container queries themselves.
 */
export const ERP_NARROW_MEDIA = `screen and (max-width: ${ERP_TABLE_MIN_WIDTH - 1}px)`;

/** Custom property carrying the grid's live column count (stamped by `ErpTable`). */
export const ERP_COL_COUNT_VAR = '--pipe-erp-col-count';
/** Optional per-screen addition to the table's min-width floor, for a grid whose one wide column
 *  (an actions cell with an input + buttons) needs more than its equal share: the screen sets it
 *  to `(wide column width - ERP_COL_MIN_WIDTH)` on its table class, so narrow viewports scroll the
 *  grid instead of squeezing every other column to a few characters. Unset = 0. */
export const ERP_EXTRA_WIDTH_VAR = '--pipe-erp-extra-width';

/**
 * Custom property carrying column `index`'s header label (1-based, stamped by `ErpTable`).
 *
 * Card mode renders it as the cell's `::before` content, so a phone reader sees "Result: Pass"
 * instead of a bare "Pass". Doing it through ONE custom property per COLUMN (not a `data-label`
 * attribute per CELL) is what keeps this O(columns) rather than O(cells) — a 2,853-row × 12-column
 * report is 34k cells, and stamping every one of them would blow the frontend perf standing rules.
 */
export const erpColLabelVar = (index: number) =>
  `--pipe-erp-col-label-${index}`;

/**
 * How many columns card mode labels. Beyond this the value still renders (unlabelled): the
 * `content: var(…)` declaration is invalid-at-computed-value-time when the property is unset, so
 * `content` falls back to `normal` and `::before` generates no box at all.
 */
export const ERP_MAX_LABELLED_COLS = 32;

// --- The card-mode SELECTOR contract ------------------------------------------------------
// Each builder takes the grid's class as a SELECTOR fragment: `erp-table.css.ts` passes the bare
// vanilla-extract class name (the stylesheet prefixes the dot itself), a spec passes a dotted one.

/**
 * Every row card mode reshapes into a card: the data rows AND the totals footer.
 *
 * `thead` is deliberately outside this set — it is the label SOURCE, not a rendered row — and it
 * has to be excluded by NAME, because AFFiNE's header cells are `td` too, never `th`.
 */
export const erpCardRowSelector = (grid: string) =>
  `${grid} :is(tbody, tfoot) tr`;

/**
 * ONE card line = one cell that IS a column.
 *
 * A `colSpan` cell is NOT a column: it is a full-width panel (today the Sites accordion's
 * members / site-details / certifications editors) or a summary band. Feeding it the label/value
 * line rules would lay its panels out as flex items and — through the `nth-child` label map below
 * — caption it with column 1's header ("Name"), i.e. a WRONG label on content that is not a value.
 * The carve-out lives here, in the shared contract, so every future colSpan row is covered without
 * a counter-rule at the call site.
 *
 * (`colspan="1"` is a no-op the browser treats as an ordinary cell, and no call site writes it; a
 * cell that did would merely lose its label — it can never gain a wrong one.)
 */
export const erpCardCellSelector = (grid: string) =>
  `${grid} :is(tbody, tfoot) td:not([colspan])`;

/** The complement of {@link erpCardCellSelector}: the spanning cell, reset to a plain block. */
export const erpCardPanelCellSelector = (grid: string) =>
  `${grid} :is(tbody, tfoot) td[colspan]`;

/**
 * Marks the cell of a totals row that carries the row's LABEL ("Totals") rather than a value.
 *
 * Card mode hides the header row and captions each cell with its column's header, so without this
 * the totals card reads "Item Barcode — Totals": the label presented as that column's VALUE, i.e.
 * a 2,854th data row whose barcode is the word "Totals". An attribute (not a class) keeps the
 * contract one grep-able token shared by `report-result.tsx` and the label map below.
 */
export const ERP_TOTALS_LABEL_ATTR = 'data-erp-totals-label';

/** Column `index`'s label map (1-based) — every card line EXCEPT a totals-row label cell. */
export const erpCardLabelSelector = (grid: string, index: number) =>
  `${erpCardCellSelector(grid)}:not([${ERP_TOTALS_LABEL_ATTR}]):nth-child(${index})`;

/**
 * "This cell hosts a CONTROL." One predicate, two consumers (SSOT):
 *
 *  • `erp-table.tsx` — targets that keep their own gesture semantics, so a copy gesture landing on
 *    one never copies and the touch gate never swallows their pointerdown;
 *  • `erp-screen.css.ts` — the cells the shared width-discipline clip must NOT apply to, because
 *    clipping a control hides an affordance rather than a few characters.
 */
export const ERP_INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [role="separator"]';

/**
 * A shared-`cell` cell that hosts a control (see {@link ERP_INTERACTIVE_SELECTOR}).
 *
 * `:has()` rather than an opt-in class at ~10 actions columns, for the same reason the clip itself
 * is not a per-call-site `ellipsis` guess: a NEW grid that forgets the class would silently clip
 * its own advance buttons. This way the exemption is a property of the cell's CONTENT and needs no
 * call-site knowledge. Support is the same generation as the container queries above (Chrome 105 /
 * Safari 15.4 / Firefox 121), and an engine without it simply drops this one rule — the cell stays
 * clipped, i.e. no worse than not having written it.
 */
export const erpControlCellSelector = (cellClass: string) =>
  `${cellClass}:has(${ERP_INTERACTIVE_SELECTOR})`;
